import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PhotographyDetailPager, { PhotographyVideoWarmup } from '@components/photographyDetailPager';
import PhotographyVideoPlayer from '@components/photographyVideoPlayer';
import { photographyVideoImage } from '@images/imageUrls';
import { isVideoWork, getPhotographyVideoUrl } from '@utils/photographyVideo';
import { protectedMediaStyle } from '@utils/contentProtection';
import styles from './index.module.scss';

interface Work {
    id: number;
    name: string;
    image: string;
    type?: string;
    video?: string | { url?: string; videoUrl?: string };
    width?: number;
    height?: number;
}

interface Props {
    works: Work[];
    initialIndex: number;
    onClose: (index: number) => void;
}

/** 影展按当前分类浏览图片和视频，关闭时将位置交还给背景轮播。 */
const PhotographyExhibitionViewer: React.FC<Props> = ({ works, initialIndex, onClose }) => {
    const [index, setIndex] = useState(initialIndex);
    const [expanded, setExpanded] = useState(false);
    const progressRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<HTMLDivElement>(null);
    const work = works[index];
    const next = works[index + 1];
    const nextVideoUrl = next && isVideoWork(next) ? getPhotographyVideoUrl(next) : '';

    useEffect(() => {
        const overflow = document.body.style.overflow;
        const focused = document.activeElement as HTMLElement | null;
        document.body.style.overflow = 'hidden';
        viewerRef.current?.focus({ preventScroll: true });
        return () => {
            document.body.style.overflow = overflow;
            if (focused?.isConnected) focused.focus({ preventScroll: true });
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            // 横屏播放器先处理自己的返回，下一次返回才关闭作品预览。
            if (expanded) return;
            if (event.key === 'Escape') { event.preventDefault(); onClose(index); }
            if (event.key === 'Tab') {
                const controls = Array.from(viewerRef.current?.querySelectorAll<HTMLElement>('button,input,[tabindex="0"]') || [])
                    .filter(element => element.tabIndex >= 0 && !element.hasAttribute('disabled') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
                const first = controls[0];
                const last = controls[controls.length - 1];
                if (!first) { event.preventDefault(); return; }
                if (document.activeElement === viewerRef.current || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
                    event.preventDefault();
                    (event.shiftKey ? last : first).focus();
                }
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [expanded, index, onClose]);

    if (!work) return null;
    const preview = (item?: Work) => item && <div className={styles.work}>
        <div className={styles.media}><img src={item.image} alt="" draggable={false} /></div>
    </div>;

    return createPortal(<div ref={viewerRef} tabIndex={-1} className={styles.viewer} role="dialog" aria-modal="true" aria-label="影展作品预览">
        <PhotographyDetailPager
            axis="horizontal"
            workKey={String(work.id)}
            previous={preview(works[index - 1])}
            next={preview(next)}
            disabled={expanded}
            onPrepare={async direction => !!works[index + direction]}
            onCommit={direction => setIndex(current => Math.max(0, Math.min(works.length - 1, current + direction)))}
        >
            <div className={styles.work}>
                <div className={styles.media}>
                    {isVideoWork(work) ? <PhotographyVideoPlayer
                        key={work.id}
                        src={getPhotographyVideoUrl(work)}
                        poster={work.image}
                        title={work.name}
                        width={work.width}
                        height={work.height}
                        progressHost={progressRef}
                        onExpandedChange={setExpanded}
                    /> : <img src={work.image} alt={work.name} draggable={false} style={protectedMediaStyle}
                        onContextMenu={event => event.preventDefault()} />}
                </div>
                <div ref={progressRef} className={styles.progress} />
            </div>
        </PhotographyDetailPager>
        <div className={styles.header}>
            <button type="button" aria-label="返回影展" className={styles.back} onClick={() => onClose(index)}>
                <img src={photographyVideoImage.back} alt="" />
            </button>
            <span className={styles.counter} aria-live="polite">{index + 1}/{works.length}</span>
        </div>
        {nextVideoUrl && <PhotographyVideoWarmup key={next.id} src={nextVideoUrl} />}
    </div>, document.body);
};

export default PhotographyExhibitionViewer;
